/**
 * Mengenangaben im Fragenkatalog (`PxAnswer.quantity`).
 *
 * Der Katalog gibt **keine** Präzision vor: eine Antwort trägt nur `withQuantity` und das
 * Freitext-Label `quantityDescription` (heute durchgehend „Menge in qm"), kein Typ- oder
 * Einheitenfeld. Der Vertrag erlaubt Dezimalstellen längst (`quantity: Float`, nicht `Int`) —
 * die Eingabe nahm sie nur nicht an: Chakras Default-`isValidCharacter`
 * (`/^[Ee0-9+\-.]$/`) verschluckt das Komma, aus „2,5" wurde „25".
 *
 * Regeln: Komma UND Punkt gelten als Dezimaltrenner (wer vom Zehnerblock tippt, tippt
 * Punkt), maximal zwei Nachkommastellen, abgeschnitten statt gerundet — und **ohne
 * Auffüllen**: „3" bleibt „3", nie „3,00". Nicht aufzufüllen ist die robuste Wahl, weil der
 * Katalog live pro Versicherer geholt wird und ein künftiges „Anzahl …"-Feld ohne
 * Typinformation ankäme.
 *
 * ## Die zwei Darstellungen — und warum sie nie vertauscht werden dürfen
 *
 * Chakras `NumberInput` hält zwei Formen desselben Wertes auseinander:
 *
 * - **Eingabeform** (`value`-Prop, „parsed"): Dezimaltrenner ist der **PUNKT**.
 * - **Anzeigeform** (was im DOM steht, „formatted"): Dezimaltrenner ist das **KOMMA**.
 *
 * Chakra rechnet ausschließlich mit der Eingabeform und zeigt `format(Eingabeform)` an.
 * Landet eine Anzeigeform (mit Komma) im `value`, zerstört Chakra den Wert beim nächsten
 * Blur: `@chakra-ui/counter` hat eine **eigene**, interne `parse`-Funktion, die die
 * `parse`-Prop ignoriert —
 *
 * ```js
 * parseFloat(value.toString().replace(/[^\w.-]+/g, ''))
 * ```
 *
 * — und das Komma ist kein `\w`, wird also *entfernt* statt als Trenner gelesen. Über
 * `clampValueOnBlur` (Default `true`) → `validateAndClamp` → `cast` wurde aus „10,45" so
 * `1045`, also der hundertfache Wert (UCPWO-398, von Micha auf Staging gefunden: „wer klaut
 * das Komma?"). Deshalb liefert {@link toQuantityInput} die **Eingabeform** — die
 * Anzeigeform entsteht ausschließlich in {@link displayQuantity} als `format`-Prop und
 * existiert nur im DOM.
 */

export const QUANTITY_DECIMALS = 2;

/** Erlaubte Eingabezeichen: Ziffern und beide Dezimaltrenner (keine Vorzeichen, kein `e`). */
export const isQuantityCharacter = (char: string): boolean => /^[0-9.,]$/.test(char);

/**
 * „2,5" und „2.5" → `2.5`. Leer, unlesbar oder ≤ 0 → `0` (das Feld gilt dann als leer).
 * Schneidet auf {@link QUANTITY_DECIMALS} Nachkommastellen ab.
 */
export const parseQuantity = (input: string): number => {
    const value = Number.parseFloat(input.replace(',', '.'));
    if (!Number.isFinite(value) || value <= 0) return 0;
    const factor = 10 ** QUANTITY_DECIMALS;
    return Math.floor(value * factor) / factor;
};

/**
 * Zahl → **Eingabeform** (Punkt als Trenner), fürs Vorbelegen und für die Normalisierung
 * beim Blur. `0` wird zu einer leeren Zeichenkette, damit ein unausgefülltes Mengenfeld
 * nicht mit „0" vorbelegt aussieht.
 *
 * Bewusst KEIN deutsches Komma: siehe Modulkopf — ein Komma im `value` lässt Chakra den
 * Wert beim Blur um den Faktor 100 verfälschen.
 */
export const toQuantityInput = (value: number): string => (value ? String(value) : '');

/**
 * `parse`-Prop: Anzeige → Eingabeform. Reiner Zeichentausch (Komma → Punkt), damit sich
 * `parse`/`format` exakt umkehren.
 */
export const parseQuantityInput = (value: string): string => value.replace(',', '.');

/**
 * `format`-Prop: Eingabe → Anzeigeform. Reiner Zeichentausch (Punkt → Komma) — **nicht**
 * numerisch: ein `format`, das den Wert auswertet, kürzt die Zwischenstufe „2," auf „2"
 * ein, das nächste Zeichen landet dann als „25" (erster Versuch dieses Fixes, auf staging
 * verifiziert).
 */
export const displayQuantity = (value: string | number): string =>
    String(value).replace('.', ',');
